import { BoltIcon } from '@heroicons/react/24/outline';
import React, { useMemo, useCallback, useState } from 'react';
import { useNavigate, useLocation, generatePath } from 'react-router-dom';

import { buildSidebarItems, buildTabBarItems } from './authenticatedNav';
import { JobCompletionToaster } from '../../components/JobCompletionToaster';
import { LiveNotificationToaster } from '../../components/LiveNotificationToaster';
import { PrivateLayout } from '../PrivateLayout';
import { PrivateHeader } from '../PrivateLayout';

import type {
  TabBarItemData,
  ActionButtonConfig,
} from '../../components/BottomTabBar';
import type { SidebarItem, SidebarBrandConfig } from '../../components/Sidebar';
import type { ID } from '@personal-trainer-app/shared/src/types/common/id.types';

import { ReadOnlyNotice } from '@/components/auth/ReadOnlyNotice';
import { PaywallProvider } from '@/components/paywall';
import { AuthSessionProvider, EchoProvider } from '@/components/providers';
import { ProtectedRoute } from '@/components/routing';
import { ShareProvider } from '@/components/ui/Share';
import { useAuth, useSignOut } from '@/hooks/auth';
import { StartWorkoutSheet } from '@/layouts/components/StartWorkoutSheet';
import { WORKOUTS_ROUTES } from '@/utils/routes';

/**
 * Default brand configuration for the sidebar
 */
const DEFAULT_BRAND: SidebarBrandConfig = {
  name: 'Dō',
  tagline: 'Personal Training',
};

/**
 * AuthenticatedLayout - Main layout for protected/authenticated routes
 *
 * Combines ProtectedRoute authentication wrapper with PrivateLayout.
 * Uses PrivateHeader for the header with integrated user/notification dropdowns.
 *
 * Navigation items are defined here with route-based active state detection.
 *
 * @example
 * ```tsx
 * // In router config:
 * {
 *   path: '/',
 *   element: <AuthenticatedLayout />,
 *   children: [
 *     { path: 'dashboard', element: <DashboardPage /> },
 *   ],
 * }
 * ```
 */
export function AuthenticatedLayout(): React.JSX.Element {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();

  // Role gating lives in the shared nav manifest, so the sidebar and the mobile
  // drawer cannot disagree about who sees Clients.
  const role = user?.role;

  const sidebarItems: SidebarItem[] = useMemo(
    () => buildSidebarItems({ role }),
    [role]
  );

  // The phone tab bar follows the SAVED dashboard profile, so it is right on
  // every page and after a reload; a switch shows once its save lands.
  const dashboardProfile = user?.preferences?.dashboard_profile;
  const tabBarItems: TabBarItemData[] = useMemo(
    () => buildTabBarItems({ profile: dashboardProfile, role }),
    [dashboardProfile, role]
  );

  // Derive active sidebar item from current route
  const activeSidebarItemId = useMemo(() => {
    const pathname = location.pathname;

    // Check for exact matches first
    for (const item of sidebarItems) {
      if ('href' in item && item.href === pathname) {
        return item.id;
      }
    }

    // Check for prefix matches (e.g., /plans/123 matches /plans)
    for (const item of sidebarItems) {
      if ('href' in item && item.href && pathname.startsWith(item.href + '/')) {
        return item.id;
      }
    }

    // Default to dashboard for root path
    if (pathname === '/') {
      return 'dashboard';
    }

    return undefined;
  }, [location.pathname, sidebarItems]);

  // Derive active tab bar item from current route
  const activeTabBarItemId = useMemo(() => {
    const pathname = location.pathname;

    // Check for exact matches first
    for (const item of tabBarItems) {
      if (item.href === pathname) {
        return item.id;
      }
    }

    // Check for prefix matches
    for (const item of tabBarItems) {
      if (item.href && pathname.startsWith(item.href + '/')) {
        return item.id;
      }
    }

    // Default to home for root path
    if (pathname === '/') {
      return 'home';
    }

    return undefined;
  }, [location.pathname, tabBarItems]);

  // Handle sidebar item click
  const handleSidebarItemClick = useCallback(
    (item: { href?: string }) => {
      if (item.href) {
        navigate(item.href);
      }
    },
    [navigate]
  );

  // Handle tab bar item click
  const handleTabBarItemClick = useCallback(
    (item: { href?: string }) => {
      if (item.href) {
        navigate(item.href);
      }
    },
    [navigate]
  );

  // Handle action button click (Start Workout).
  //
  // The bolt opens the sheet rather than navigating: what to start is a decision
  // with several possible answers, and a route would have to guess one before it
  // could render. The sheet asks the API once and shows the answer in place.
  const [startSheetOpen, setStartSheetOpen] = useState(false);

  const handleStartWorkout = useCallback(() => {
    setStartSheetOpen(true);
  }, []);

  // Straight to the session. If an unfinished one is already in progress, the
  // session page's own resume gate meets the user there — the sheet does not
  // duplicate it.
  const handleStartSelected = useCallback(
    (workoutId: ID) => {
      setStartSheetOpen(false);
      navigate(
        generatePath(WORKOUTS_ROUTES.SESSION, { id: String(workoutId) })
      );
    },
    [navigate]
  );

  const handleCreateWorkout = useCallback(() => {
    setStartSheetOpen(false);
    navigate(WORKOUTS_ROUTES.NEW);
  }, [navigate]);

  // Configure center action button for tab bar
  const tabBarActionButton: ActionButtonConfig = useMemo(
    () => ({
      icon: <BoltIcon className="h-6 w-6" />,
      onClick: handleStartWorkout,
      ariaLabel: 'Start Workout',
    }),
    [handleStartWorkout]
  );

  // Logout from the drawer — or leave, for an admin signed in as the member.
  const { signOut: handleLogout, label: logoutLabel } = useSignOut();

  return (
    <ProtectedRoute>
      <AuthSessionProvider>
        <EchoProvider>
          <JobCompletionToaster />
          <ReadOnlyNotice />
          <ShareProvider>
            <PaywallProvider>
              <LiveNotificationToaster />
              <PrivateLayout
                header={<PrivateHeader />}
                sidebarItems={sidebarItems}
                sidebarBrand={DEFAULT_BRAND}
                activeSidebarItemId={activeSidebarItemId}
                onSidebarItemClick={handleSidebarItemClick}
                tabBarItems={tabBarItems}
                activeTabBarItemId={activeTabBarItemId}
                onTabBarItemClick={handleTabBarItemClick}
                tabBarActionButton={tabBarActionButton}
                onLogout={handleLogout}
                logoutLabel={logoutLabel}
              />
              <StartWorkoutSheet
                isOpen={startSheetOpen}
                onClose={() => setStartSheetOpen(false)}
                onStart={handleStartSelected}
                onCreateWorkout={handleCreateWorkout}
              />
            </PaywallProvider>
          </ShareProvider>
        </EchoProvider>
      </AuthSessionProvider>
    </ProtectedRoute>
  );
}

export default AuthenticatedLayout;
